/**
 * Version-gated feature toggle helper.
 *
 * Some features ship to the app before the backend flips them on. Instead of a
 * plain y/n toggle, the backend stores the minimum app version at which the
 * feature turns on, and the feature is enabled when the running app version is
 * greater than or equal to it (app version >= minimum). Mirrors the app's
 * VersionCompareHelper.compareAppVersionGreaterEqualThanToggleVersion.
 *
 * - Non-app web (no AppVersion cookie) or a blank minimum => disabled.
 * - Equal version => enabled (>=).
 * - Versions are dotted numeric ("8.6.0"); any "-suffix" or "+build" is dropped
 *   before comparison, and shorter versions are padded with zeros
 *   ("8.6" == "8.6.0").
 */
export function isAppVersionGreaterThanOrEqual(appVersion: string, minVersion: string): boolean {
    if (!appVersion || !minVersion) return false;

    const current = parseVersionParts(appVersion);
    const minimum = parseVersionParts(minVersion);
    const length = Math.max(current.length, minimum.length);

    for (let i = 0; i < length; i += 1) {
        const c = current[i] ?? 0;
        const m = minimum[i] ?? 0;
        if (c !== m) return c > m;
    }

    return true; // equal => enabled (>=)
}

function parseVersionParts(version: string): number[] {
    return version
        .split(/[-+]/)[0]
        .split('.')
        .map(part => Number.parseInt(part) || 0);
}
